<template>
  <div v-if="visible" class="list-merge-root">
    <div class="modal fade show list-merge-modal" tabindex="-1" role="dialog" aria-modal="true" style="display: block">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-object-group me-2"></i>{{ $t("listMerge.title") }}
              <small class="text-muted ms-2">{{ listName }}</small>
            </h5>
            <button type="button" class="btn-close" :disabled="isExecuting" @click="close"></button>
          </div>

          <!-- Paso 1: armar los grupos -->
          <div v-if="step === 'build'" class="modal-body">
            <p class="text-muted mb-3">{{ $t("listMerge.intro") }}</p>

            <div class="list-merge-toolbar">
              <button class="btn btn-sm btn-outline-primary" @click="suggestSameName">
                <i class="fas fa-equals me-1"></i>{{ $t("listMerge.sameName") }}
              </button>
              <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-primary" @click="suggestSimilar">
                  <i class="fas fa-magic me-1"></i>{{ $t("listMerge.similarName") }}
                </button>
                <select v-model.number="similarity" class="form-select form-select-sm list-merge-threshold" :title="$t('listMerge.similarityTitle')">
                  <option :value="0.95">95%</option>
                  <option :value="0.9">90%</option>
                  <option :value="0.85">85%</option>
                  <option :value="0.8">80%</option>
                  <option :value="0.7">70%</option>
                </select>
              </div>
              <button class="btn btn-sm" :class="showPicker ? 'btn-secondary' : 'btn-outline-secondary'" @click="showPicker = !showPicker">
                <i class="fas fa-hand-pointer me-1"></i>{{ $t("listMerge.pickByHand") }}
              </button>
              <span class="list-merge-toolbar__hint">{{ $t("listMerge.toolbarHint") }}</span>
            </div>

            <div v-if="suggestionMessage" class="alert alert-info py-2 small mt-2 mb-0">{{ suggestionMessage }}</div>

            <!-- Selección manual -->
            <div v-if="showPicker" ref="picker" class="list-merge-picker">
              <div class="d-flex flex-wrap gap-2 mb-2">
                <input ref="pickerSearch" v-model="pickerSearch" class="form-control form-control-sm list-merge-picker__search" :placeholder="$t('listMerge.searchPlaceholder')" />
                <select v-model="pickerDestination" class="form-select form-select-sm list-merge-picker__destination">
                  <option value="new">{{ $t("listMerge.destinationNew") }}</option>
                  <option v-for="(group, index) in groups" :key="group.key" :value="group.key">
                    {{ $t("listMerge.destinationGroup", { n: index + 1, label: termById[group.targetId].label }) }}
                  </option>
                </select>
                <button class="btn btn-sm btn-primary text-nowrap" :disabled="!canApplyPicker" @click="applyPicker">
                  <i class="fas fa-plus me-1"></i>{{ pickerButtonText }}
                </button>
              </div>
              <div class="list-merge-picker__list">
                <label v-for="term in pickerTerms" :key="term.id" class="list-merge-picker__item" :class="{ 'is-grouped': groupedIds.has(term.id) }">
                  <input v-model="pickerSelection" type="checkbox" class="form-check-input me-2" :value="term.id" :disabled="groupedIds.has(term.id)" />
                  <span class="list-merge-term">
                    <span v-if="term.path" class="list-merge-term__path">{{ term.path }} ›</span>
                    {{ term.label }}
                  </span>
                  <span class="list-merge-usage" :class="{ 'is-zero': !term.usage }">{{ term.usage }}</span>
                  <span v-if="groupedIds.has(term.id)" class="badge bg-secondary ms-2">{{ $t("listMerge.alreadyInGroup") }}</span>
                </label>
                <div v-if="pickerTerms.length === 0" class="text-muted small p-2">{{ $t("listMerge.noTermsFound") }}</div>
              </div>
            </div>

            <!-- Grupos -->
            <div class="list-merge-groups">
              <div v-if="groups.length === 0" class="list-merge-empty">
                <i class="fas fa-layer-group fa-2x mb-2"></i>
                <div>{{ $t("listMerge.noGroups") }}</div>
              </div>
              <div v-for="(group, index) in groups" :key="group.key" class="list-merge-group">
                <div class="list-merge-group__header">
                  <strong>{{ $t("listMerge.groupN", { n: index + 1 }) }}</strong>
                  <span class="text-muted small ms-2">{{ groupOriginLabel(group) }}</span>
                  <button class="btn btn-sm ms-auto" :class="group.editing ? 'btn-secondary' : 'btn-outline-secondary'" @click="toggleEditing(group)">
                    <i class="fas me-1" :class="group.editing ? 'fa-check' : 'fa-pen'"></i>{{ group.editing ? $t("listMerge.doneEditing") : $t("listMerge.editGroup") }}
                  </button>
                  <button class="btn btn-sm btn-link text-danger" @click="removeGroup(index)">
                    <i class="fas fa-times me-1"></i>{{ $t("listMerge.discardGroup") }}
                  </button>
                </div>
                <table class="table table-sm mb-2 list-merge-members">
                  <thead>
                    <tr>
                      <th class="text-center" style="width: 70px">{{ $t("listMerge.keeps") }}</th>
                      <th class="text-center" style="width: 70px">{{ $t("listMerge.include") }}</th>
                      <th>{{ $t("listMerge.term") }}</th>
                      <th class="text-end" style="width: 90px">{{ $t("listMerge.uses") }}</th>
                      <th v-if="group.editing" style="width: 40px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="memberId in group.memberIds" :key="memberId" :class="{ 'is-target': group.targetId === memberId, 'is-excluded': group.excluded.includes(memberId) }">
                      <td class="text-center">
                        <input type="radio" class="form-check-input" :name="`target-${group.key}`" :checked="group.targetId === memberId" :disabled="group.excluded.includes(memberId)" @change="setTarget(group, memberId)" />
                      </td>
                      <td class="text-center">
                        <input type="checkbox" class="form-check-input" :checked="!group.excluded.includes(memberId)" :disabled="group.targetId === memberId" @change="toggleExcluded(group, memberId)" />
                      </td>
                      <td>
                        <span class="list-merge-term">
                          <span v-if="termById[memberId].path" class="list-merge-term__path">{{ termById[memberId].path }} ›</span>
                          {{ termById[memberId].label }}
                        </span>
                        <span v-if="group.targetId === memberId" class="badge bg-success ms-2">{{ $t("listMerge.keepsBadge") }}</span>
                        <span v-else-if="!group.excluded.includes(memberId)" class="badge bg-danger ms-2">{{ $t("listMerge.mergedBadge") }}</span>
                      </td>
                      <td class="text-end">
                        <span class="list-merge-usage" :class="{ 'is-zero': !termById[memberId].usage }">{{ termById[memberId].usage }}</span>
                      </td>
                      <td v-if="group.editing" class="text-center">
                        <button class="btn btn-sm btn-link text-danger p-0" :title="$t('listMerge.removeFromGroup')" @click="removeMember(group, memberId)">
                          <i class="fas fa-minus-circle"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="group.editing" class="text-muted small mb-2">
                  <i class="fas fa-info-circle me-1"></i>{{ $t("listMerge.editingHint") }}
                </div>
                <div class="list-merge-group__rename">
                  <label class="form-label small mb-1">{{ $t("listMerge.finalName") }}</label>
                  <input v-model="group.label" class="form-control form-control-sm" :placeholder="termById[group.targetId].label" />
                </div>
                <div v-if="activeSources(group).length === 0" class="text-warning small mt-1">
                  <i class="fas fa-exclamation-triangle me-1"></i>{{ $t("listMerge.groupNeedsTwo") }}
                </div>
              </div>
            </div>

            <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>
          </div>

          <!-- Paso 2: confirmación -->
          <div v-else-if="step === 'confirm'" class="modal-body">
            <div class="alert alert-warning">
              <i class="fas fa-exclamation-triangle me-2"></i>
              <span v-html="confirmHeadline"></span>
            </div>

            <div v-for="(group, index) in preview.groups" :key="group.target._id" class="list-merge-confirm-group">
              <div class="list-merge-confirm-group__title">
                <span class="text-muted me-2">{{ index + 1 }}.</span>
                <span v-for="source in group.sources" :key="source._id" class="list-merge-chip is-source">{{ termLabelWithPath(source) }}</span>
                <i class="fas fa-long-arrow-alt-right mx-2"></i>
                <span class="list-merge-chip is-target">{{ group.newLabel || termLabelWithPath(group.target) }}</span>
                <span v-if="group.newLabel" class="text-muted small ms-2">({{ $t("listMerge.renamedFrom", { label: group.target.label }) }})</span>
              </div>

              <div v-if="group.references.length" class="list-merge-confirm-refs">
                <div v-for="ref in group.references" :key="`${ref.collection}|${ref.field}`" class="list-merge-confirm-ref" :class="{ 'is-legacy': ref.legacy }">
                  <span>{{ ref.modelLabel }} · {{ ref.fieldLabel }}</span>
                  <span v-if="ref.legacy" class="text-muted small ms-1">({{ $t("listMerge.legacyCopy") }})</span>
                  <span class="ms-auto">{{ $tc("listMerge.nRecords", ref.documents, { count: ref.documents }) }}</span>
                </div>
              </div>
              <div v-else class="text-muted small">{{ $t("listMerge.noRecords") }}</div>
              <div v-if="group.deletedDocuments" class="text-muted small mt-1">
                {{ $t("listMerge.includesTrash", { count: group.deletedDocuments }) }}
              </div>
              <div v-if="group.reparented.length" class="small mt-1">
                <i class="fas fa-sitemap me-1"></i>{{ $t("listMerge.childrenMoved", { children: group.reparented.join(", ") }) }}
              </div>

              <div v-for="(warning, wi) in group.warnings" :key="wi" class="list-merge-warning">
                <i class="fas fa-exclamation-circle me-1"></i>{{ warningText(warning) }}
              </div>
            </div>

            <ul class="small text-muted mt-3 mb-3">
              <li>{{ $t("listMerge.noteUndo") }}</li>
              <li>{{ $t("listMerge.noteOpenForms") }}</li>
            </ul>

            <div class="form-check">
              <input id="list-merge-ack" v-model="acknowledged" type="checkbox" class="form-check-input" />
              <label for="list-merge-ack" class="form-check-label fw-semibold">
                {{ $t("listMerge.acknowledge", { terms: preview.totals.termsRemoved, records: preview.totals.documents }) }}
              </label>
            </div>
            <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>
          </div>

          <!-- Paso 3: resultado -->
          <div v-else-if="step === 'done'" class="modal-body text-center py-4">
            <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
            <h5>{{ $t("listMerge.doneTitle") }}</h5>
            <p class="mb-1">{{ $t("listMerge.doneBody", { terms: result.summary.totals.termsRemoved, fields: result.modified }) }}</p>
            <p v-if="result.skipped" class="text-warning small">{{ $t("listMerge.doneSkipped", { count: result.skipped }) }}</p>
            <p class="text-muted small">{{ $t("listMerge.doneUndoHint") }}</p>
          </div>

          <div class="modal-footer">
            <template v-if="step === 'build'">
              <span class="me-auto text-muted small">{{ buildFooterText }}</span>
              <button class="btn btn-secondary" @click="close">{{ $t("Cancel") }}</button>
              <button class="btn btn-primary" :disabled="!readyGroups.length || isLoadingPreview" @click="goToConfirm">
                <i class="fas me-1" :class="isLoadingPreview ? 'fa-spinner fa-spin' : 'fa-search'"></i>{{ $t("listMerge.review") }}
              </button>
            </template>
            <template v-else-if="step === 'confirm'">
              <button class="btn btn-secondary me-auto" :disabled="isExecuting" @click="step = 'build'">
                <i class="fas fa-arrow-left me-1"></i>{{ $t("listMerge.back") }}
              </button>
              <button class="btn btn-secondary" :disabled="isExecuting" @click="close">{{ $t("Cancel") }}</button>
              <button class="btn btn-danger" :disabled="!acknowledged || isExecuting" @click="execute">
                <i class="fas me-1" :class="isExecuting ? 'fa-spinner fa-spin' : 'fa-object-group'"></i>{{ $t("listMerge.confirm") }}
              </button>
            </template>
            <template v-else>
              <button class="btn btn-primary" @click="close">{{ $t("listMerge.close") }}</button>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { buildSameNameGroups, buildSimilarNameGroups } from "@/utils/listTermSimilarity";

let groupKeySeq = 0;

export default {
  name: "ListMergeModal",
  props: {
    visible: { type: Boolean, default: false },
    listId: { type: String, required: true },
    listName: { type: String, default: "" },
    // Árbol de valores tal como lo arma list-edit (nodos con children)
    values: { type: Array, default: () => [] },
    usageCounts: { type: Object, default: () => ({}) },
  },
  emits: ["close", "merged"],
  data() {
    return {
      step: "build",
      groups: [],
      showPicker: false,
      pickerSearch: "",
      pickerSelection: [],
      // "new" crea un grupo con lo seleccionado; si no, es la key del grupo al que se suma
      pickerDestination: "new",
      similarity: 0.9,
      suggestionMessage: "",
      preview: null,
      acknowledged: false,
      isLoadingPreview: false,
      isExecuting: false,
      result: null,
      error: "",
    };
  },
  computed: {
    // Términos en plano, en el orden del árbol, con el camino de padres para distinguir homónimos
    terms() {
      const out = [];
      const walk = (nodes, path, parentId) => {
        (nodes || []).forEach((node) => {
          const id = String(node._id);
          out.push({
            id,
            label: node.label,
            path: path.join(" › "),
            parentId,
            usage: this.usageCounts[id] || 0,
            order: out.length,
          });
          walk(node.children, [...path, node.label], id);
        });
      };
      walk(this.values, [], null);
      return out;
    },
    termById() {
      return Object.fromEntries(this.terms.map((t) => [t.id, t]));
    },
    groupedIds() {
      return new Set(this.groups.flatMap((g) => g.memberIds));
    },
    pickerTerms() {
      const q = this.normalize(this.pickerSearch);
      if (!q) return this.terms;
      return this.terms.filter((t) => this.normalize(`${t.path} ${t.label}`).includes(q));
    },
    canApplyPicker() {
      const n = this.pickerSelection.length;
      return this.pickerDestination === "new" ? n >= 2 : n >= 1;
    },
    pickerButtonText() {
      const count = this.pickerSelection.length;
      return this.pickerDestination === "new"
        ? this.$t("listMerge.createGroup", { count })
        : this.$t("listMerge.addToGroup", { count });
    },
    readyGroups() {
      return this.groups.filter((g) => this.activeSources(g).length > 0);
    },
    buildFooterText() {
      const terms = this.readyGroups.reduce((n, g) => n + this.activeSources(g).length, 0);
      return this.$t("listMerge.buildFooter", { groups: this.readyGroups.length, terms });
    },
    confirmHeadline() {
      const t = this.preview.totals;
      return this.$t("listMerge.confirmHeadline", {
        groups: t.groups,
        terms: t.termsRemoved,
        records: t.documents,
      });
    },
  },
  watch: {
    visible(value) {
      if (value) this.reset();
    },
  },
  methods: {
    reset() {
      Object.assign(this.$data, this.$options.data.call(this));
    },
    close() {
      if (this.isExecuting) return;
      this.$emit("close");
    },
    normalize(text) {
      return String(text || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim();
    },
    // El que queda por defecto es el más usado (ante empate, el primero del listado)
    pickDefaultTarget(memberIds) {
      return [...memberIds].sort((a, b) => {
        const ta = this.termById[a];
        const tb = this.termById[b];
        return tb.usage - ta.usage || ta.order - tb.order;
      })[0];
    },
    addGroup(memberIds, origin) {
      const ids = memberIds.filter((id) => this.termById[id] && !this.groupedIds.has(id));
      if (ids.length < 2) return false;
      const ordered = [...ids].sort((a, b) => this.termById[a].order - this.termById[b].order);
      this.groups.push({
        key: ++groupKeySeq,
        origin,
        memberIds: ordered,
        targetId: this.pickDefaultTarget(ordered),
        excluded: [],
        label: "",
        editing: false,
      });
      return true;
    },
    // Editar un grupo: muestra el botón para sacar términos y abre «Elegir a mano» con
    // ese grupo como destino, que es donde se suman términos
    toggleEditing(group) {
      group.editing = !group.editing;
      if (group.editing) {
        this.showPicker = true;
        this.pickerDestination = group.key;
        this.$nextTick(() => {
          this.$refs.picker?.scrollIntoView({ behavior: "smooth", block: "start" });
          this.$refs.pickerSearch?.focus();
        });
      } else if (this.pickerDestination === group.key) {
        this.pickerDestination = "new";
      }
    },
    // Sacar un término lo devuelve al listado de libres; si era el que quedaba, se elige
    // otro con el mismo criterio de siempre (el más usado)
    removeMember(group, termId) {
      group.memberIds = group.memberIds.filter((id) => id !== termId);
      group.excluded = group.excluded.filter((id) => id !== termId);
      if (group.memberIds.length === 0) {
        if (this.pickerDestination === group.key) this.pickerDestination = "new";
        this.groups = this.groups.filter((g) => g !== group);
        return;
      }
      if (group.targetId === termId) {
        const candidates = group.memberIds.filter((id) => !group.excluded.includes(id));
        group.targetId = this.pickDefaultTarget(candidates.length ? candidates : group.memberIds);
        group.excluded = group.excluded.filter((id) => id !== group.targetId);
      }
    },
    addSuggestedGroups(groupsOfIds, origin) {
      const added = groupsOfIds.filter((ids) => this.addGroup(ids, origin)).length;
      this.suggestionMessage = added
        ? this.$t("listMerge.suggestionsAdded", { count: added })
        : this.$t("listMerge.noSuggestions");
    },
    suggestSameName() {
      this.addSuggestedGroups(buildSameNameGroups(this.terms), "same");
    },
    suggestSimilar() {
      this.addSuggestedGroups(buildSimilarNameGroups(this.terms, this.similarity), "similar");
    },
    applyPicker() {
      const ids = this.pickerSelection.filter((id) => !this.groupedIds.has(id));
      if (this.pickerDestination === "new") {
        if (!this.addGroup(ids, "manual")) return;
      } else {
        const group = this.groups.find((g) => g.key === this.pickerDestination);
        if (!group || ids.length === 0) return;
        group.memberIds = [...group.memberIds, ...ids].sort(
          (a, b) => this.termById[a].order - this.termById[b].order
        );
      }
      this.pickerSelection = [];
      this.pickerSearch = "";
    },
    removeGroup(index) {
      if (this.pickerDestination === this.groups[index].key) this.pickerDestination = "new";
      this.groups.splice(index, 1);
    },
    setTarget(group, memberId) {
      group.targetId = memberId;
      group.excluded = group.excluded.filter((id) => id !== memberId);
    },
    toggleExcluded(group, memberId) {
      if (group.excluded.includes(memberId)) {
        group.excluded = group.excluded.filter((id) => id !== memberId);
      } else {
        group.excluded = [...group.excluded, memberId];
      }
    },
    activeSources(group) {
      return group.memberIds.filter((id) => id !== group.targetId && !group.excluded.includes(id));
    },
    groupOriginLabel(group) {
      return this.$t(`listMerge.origin.${group.origin}`);
    },
    payloadGroups() {
      return this.readyGroups.map((group) => ({
        targetId: group.targetId,
        sourceIds: this.activeSources(group),
        label: group.label.trim() && group.label.trim() !== this.termById[group.targetId].label ? group.label.trim() : null,
      }));
    },
    async goToConfirm() {
      this.error = "";
      this.isLoadingPreview = true;
      try {
        const { data } = await wencesApi.post(`/api/gotrading/lists/${this.listId}/merge/preview`, {
          groups: this.payloadGroups(),
        });
        this.preview = data;
        this.acknowledged = false;
        this.step = "confirm";
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
      } finally {
        this.isLoadingPreview = false;
      }
    },
    async execute() {
      this.error = "";
      this.isExecuting = true;
      try {
        const { data } = await wencesApi.post(`/api/gotrading/lists/${this.listId}/merge`, {
          groups: this.payloadGroups(),
        });
        this.result = data;
        this.step = "done";
        this.$emit("merged", data);
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
      } finally {
        this.isExecuting = false;
      }
    },
    termLabelWithPath(term) {
      return term.parentLabel ? `${term.parentLabel} › ${term.label}` : term.label;
    },
    warningText(warning) {
      const list = (warning.sources || [])
        .map((s) => `${s.label}: ${s.value ?? this.$t("listMerge.none")}`)
        .join(", ");
      const target = warning.target ?? this.$t("listMerge.none");
      switch (warning.type) {
        case "accounting_code":
          return this.$t("listMerge.warnAccountingCode", { target, list });
        case "conversionRate":
          return this.$t("listMerge.warnConversionRate", { target, list });
        case "color":
          return this.$t("listMerge.warnColor", { target, list });
        case "parent":
          return this.$t("listMerge.warnParent", { target, list });
        case "definitionDefault":
          return this.$t("listMerge.warnDefinitionDefault", {
            fields: warning.references.map((r) => `${r.modelName} · ${r.fieldLabel}`).join(", "),
          });
        default:
          return "";
      }
    },
  },
};
</script>

<style scoped>
.list-merge-root {
  position: relative;
  z-index: 1060;
}
.list-merge-modal {
  z-index: 1065;
}
.list-merge-root > .modal-backdrop {
  z-index: 1060;
}
.list-merge-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.list-merge-toolbar__hint {
  font-size: 0.8rem;
  color: #6c757d;
}
.list-merge-threshold {
  width: auto;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.list-merge-picker {
  margin-top: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px;
  background: #fafafa;
}
.list-merge-picker__list {
  max-height: 260px;
  overflow-y: auto;
}
.list-merge-picker__item {
  display: flex;
  align-items: center;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  margin: 0;
}
.list-merge-picker__item:hover {
  background: #eef2ff;
}
.list-merge-picker__item.is-grouped {
  opacity: 0.5;
  cursor: default;
}
.list-merge-term__path {
  color: #6c757d;
  font-size: 0.85em;
}
.list-merge-usage {
  margin-left: auto;
  min-width: 32px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  background: #d1e7dd;
  color: #0f5132;
}
.list-merge-usage.is-zero {
  background: #fff3cd;
  color: #664d03;
}
.list-merge-groups {
  margin-top: 16px;
}
.list-merge-empty {
  text-align: center;
  color: #adb5bd;
  padding: 32px 0;
}
.list-merge-group {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
}
.list-merge-group__header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.list-merge-members tr.is-target td {
  background: #f0fdf4;
}
.list-merge-members tr.is-excluded td {
  opacity: 0.45;
}
.list-merge-picker__search {
  flex: 1 1 220px;
  width: auto;
}
.list-merge-picker__destination {
  flex: 0 1 260px;
  width: auto;
}
.list-merge-group__rename {
  max-width: 420px;
}
.list-merge-confirm-group {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  background: #fafafa;
}
.list-merge-confirm-group__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
}
.list-merge-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}
.list-merge-chip.is-source {
  background: #f8d7da;
  color: #842029;
  text-decoration: line-through;
}
.list-merge-chip.is-target {
  background: #d1e7dd;
  color: #0f5132;
}
.list-merge-confirm-refs {
  font-size: 0.85rem;
}
.list-merge-confirm-ref {
  display: flex;
  padding: 2px 0;
  border-bottom: 1px dashed #e5e7eb;
}
.list-merge-confirm-ref.is-legacy {
  color: #9ca3af;
}
.list-merge-warning {
  margin-top: 6px;
  font-size: 0.85rem;
  color: #92400e;
  background: #fef3c7;
  border-radius: 6px;
  padding: 4px 8px;
}
</style>
